import { useEffect, useState, useRef } from "react";
import { ChevronDown, Plus, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/utils/formatINR";
import DecimalInput from "@/components/DecimalInput";

function similarity(a, b) {
  const x = (a || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const y = (b || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!x || !y) return 0;
  if (x === y) return 1;
  if (x.includes(y) || y.includes(x)) return 0.85;
  const ta = new Set(x.match(/.{1,2}/g) || []);
  const tb = new Set(y.match(/.{1,2}/g) || []);
  const overlap = [...ta].filter((t) => tb.has(t)).length;
  return (2 * overlap) / (ta.size + tb.size || 1);
}

export default function MaterialGradePicker({ value, onChange, onMaterialSelect }) {
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ category: "", grade: "", density_g_cc: 7.85, default_rm_rate_per_kg: 0 });
  const ref = useRef(null);

  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  }, []);
  useEffect(() => { setSearch(value || ""); }, [value]);
  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = materials.filter((m) =>
    !search || m.grade.toLowerCase().includes(search.toLowerCase())
  );
  const exact = materials.find((m) => m.grade.toLowerCase() === (search || "").toLowerCase());
  const similar = !exact && search
    ? materials.find((m) => similarity(m.grade, search) >= 0.7 && m.grade.toLowerCase() !== search.toLowerCase())
    : null;

  function selectMaterial(m) {
    setSearch(m.grade);
    setOpen(false);
    onChange && onChange(m.grade);
    onMaterialSelect && onMaterialSelect(m);
  }

  async function createMaterial() {
    try {
      const res = await api.post("/materials", { ...createForm, grade: createForm.grade || search });
      setMaterials((prev) => [...prev, res.data]);
      selectMaterial(res.data);
      setShowCreate(false);
      setCreateForm({ category: "", grade: "", density_g_cc: 7.85, default_rm_rate_per_kg: 0 });
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create material");
    }
  }

  return (
    <div ref={ref} className="relative" data-testid="material-grade-picker">
      <div className="relative">
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); onChange && onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Type or pick a grade…"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
        />
        <ChevronDown size={16} className="absolute right-2 top-2.5 text-slate-400 pointer-events-none" />
      </div>

      {similar && !showCreate && (
        <div className="mt-1 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
          <AlertTriangle size={14} />
          <span>Did you mean</span>
          <button type="button" onClick={() => selectMaterial(similar)} className="font-semibold underline">{similar.grade}</button>
          <span>?</span>
        </div>
      )}

      {open && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-sm text-slate-400 text-center">No matches</div>
          )}
          {filtered.map((m) => (
            <div
              key={m.material_id}
              onClick={() => selectMaterial(m)}
              className="px-3 py-2 hover:bg-orange-50 cursor-pointer text-sm border-b border-slate-100 last:border-0"
            >
              <div className="font-medium text-slate-900">{m.grade}</div>
              <div className="text-xs text-slate-500">{m.category} • {formatINR(m.default_rm_rate_per_kg)}/kg • ρ {m.density_g_cc}</div>
            </div>
          ))}
          {!exact && (search || "").length >= 2 && (
            <button
              type="button"
              onClick={() => { setShowCreate(true); setCreateForm((p) => ({ ...p, grade: search })); setOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2 border-t"
            >
              <Plus size={14} /> Add <strong>{search}</strong> as a new material
            </button>
          )}
        </div>
      )}

      {showCreate && (
        <div className="mt-2 p-3 border-2 border-orange-200 rounded-md bg-orange-50/50 text-sm space-y-2">
          <div className="font-semibold text-orange-700">Create new material</div>
          <input className="w-full px-2 py-1 border rounded text-sm" placeholder="Category (e.g. CR Steel)"
                 value={createForm.category} onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })} />
          <input className="w-full px-2 py-1 border rounded text-sm" placeholder="Grade"
                 value={createForm.grade} onChange={(e) => setCreateForm({ ...createForm, grade: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <DecimalInput className="px-2 py-1 border rounded text-sm" placeholder="Density g/cc"
                   value={createForm.density_g_cc} onChange={(v) => setCreateForm({ ...createForm, density_g_cc: v })} />
            <DecimalInput className="px-2 py-1 border rounded text-sm" placeholder="Rate ₹/kg"
                   value={createForm.default_rm_rate_per_kg} onChange={(v) => setCreateForm({ ...createForm, default_rm_rate_per_kg: v })} />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-1 text-sm">Cancel</button>
            <button type="button" onClick={createMaterial} className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700">Create</button>
          </div>
        </div>
      )}
    </div>
  );
}
